/**
 * Buyer asks a supplier for a revised quote (negotiation round).
 *
 * The message is what the supplier reads; the two targets are optional and
 * shown to the supplier alongside it. On success the response reopens for
 * the supplier and the current quote is kept as history.
 */
import { useState } from 'react';
import { rfqAPI } from '../../lib/api';
import type { SupplierSummary } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2, MessageSquareReply } from 'lucide-react';
import { toast } from 'sonner';

interface RequestRevisionDialogProps {
  /** The supplier's response row id (rfq responses endpoint). */
  responseId: number;
  supplier: SupplierSummary;
  currency: string;
  /** The supplier's current grand total, already formatted for display. */
  currentTotal: string;
  onClose: () => void;
  /** Called after the request is accepted so the parent can refetch. */
  onRequested: () => void;
}

export default function RequestRevisionDialog({ responseId, supplier, currency, currentTotal, onClose, onRequested }: RequestRevisionDialogProps) {
  const [message, setMessage] = useState('');
  const [targetTotal, setTargetTotal] = useState('');
  const [targetLeadTime, setTargetLeadTime] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('Tell the supplier what you would like them to reconsider.');
      return;
    }
    try {
      setSubmitting(true);
      await rfqAPI.requestRevision(responseId, {
        message: message.trim(),
        target_total: targetTotal.trim() === '' ? null : Number(targetTotal),
        target_lead_time_days: targetLeadTime.trim() === '' ? null : Number(targetLeadTime),
      });
      toast.success(`Revised quote requested from ${supplier.name}`);
      onRequested();
      onClose();
    } catch (err: unknown) {
      console.error('Error requesting a revised quote:', err);
      toast.error(getApiErrorMessage(err, 'Could not request a revised quote'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !submitting) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareReply className="h-5 w-5" />
            Request a revised quote
          </DialogTitle>
          <DialogDescription>
            {supplier.name} will be notified and can edit and resubmit their quote. Their current quote
            ({currentTotal}) is kept on record so you can see what changed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="revision-message">What should they reconsider? *</Label>
            <Textarea
              id="revision-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Your unit price on line 2 is above our target. Can you improve it, or offer a better lead time on line 1?"
              rows={4}
              maxLength={2000}
              disabled={submitting}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="revision-target-total">Target total ({currency})</Label>
              <Input
                id="revision-target-total"
                type="number"
                min="0"
                step="0.01"
                value={targetTotal}
                onChange={(e) => setTargetTotal(e.target.value)}
                placeholder="Optional"
                disabled={submitting}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="revision-target-lead">Target lead time (days)</Label>
              <Input
                id="revision-target-lead"
                type="number"
                min="0"
                step="1"
                value={targetLeadTime}
                onChange={(e) => setTargetLeadTime(e.target.value)}
                placeholder="Optional"
                disabled={submitting}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || !message.trim()}>
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageSquareReply className="h-4 w-4 mr-2" />}
            Send request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
